// src/pages/Sitemap.js

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiHome, FiPackage, FiHelpCircle, FiShield, FiFileText, FiMap } from 'react-icons/fi';
import SEO from '../components/SEO';

const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(135deg, #1A365D 0%, #0B1D3A 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(135deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(225deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(315deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%);
    background-size: 60px 60px;
    opacity: 0.4;
    z-index: 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem var(--spacing-md) 4rem;
  position: relative;
  z-index: 1;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  background: rgba(0, 0, 0, 0.2);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-sm);
  backdrop-filter: blur(8px);
  transition: all var(--transition-fast);
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-decoration: none;
  margin-bottom: var(--spacing-lg);

  svg {
    margin-right: var(--spacing-xs);
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-xl);
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: var(--spacing-xl) var(--spacing-md);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-sm);
  color: var(--white);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
`;

const SitemapGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--spacing-lg);
`;

const SitemapSection = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  border-bottom: 2px solid var(--accent);

  svg {
    font-size: 1.5rem;
    color: var(--accent);
    margin-right: var(--spacing-sm);
  }

  h2 {
    color: var(--primary);
    font-size: 1.3rem;
    margin: 0;
  }
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const LinkItem = styled.li`
  margin-bottom: var(--spacing-sm);

  a {
    color: var(--text-secondary);
    text-decoration: none;
    display: flex;
    align-items: center;
    padding: var(--spacing-xs) 0;
    transition: all var(--transition-fast);

    &:hover {
      color: var(--accent);
      transform: translateX(4px);
    }

    &::before {
      content: '→';
      margin-right: var(--spacing-xs);
      color: var(--accent);
    }
  }
`;

const Sitemap = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <>
      <SEO routeKey="sitemap" />
      <PageContainer>
        <ContentWrapper>
        <BackButton to="/">
          <FiChevronLeft /> Back to Home
        </BackButton>

        <PageHeader>
          <PageTitle>Sitemap</PageTitle>
          <PageSubtitle>Complete overview of XADevs website structure</PageSubtitle>
        </PageHeader>

        <SitemapGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Main Pages */}
          <SitemapSection variants={itemVariants}>
            <SectionHeader>
              <FiHome />
              <h2>Main Pages</h2>
            </SectionHeader>
            <LinkList>
              <LinkItem><Link to="/">Home</Link></LinkItem>
              <LinkItem><Link to="/about">About XADevs</Link></LinkItem>
              <LinkItem><Link to="/#products">Products</Link></LinkItem>
              <LinkItem><Link to="/#contact">Contact</Link></LinkItem>
              <LinkItem><Link to="/support">Support Center</Link></LinkItem>
            </LinkList>
          </SitemapSection>

          {/* Products */}
          <SitemapSection variants={itemVariants}>
            <SectionHeader>
              <FiPackage />
              <h2>Our Products</h2>
            </SectionHeader>
            <LinkList>
              <LinkItem><Link to="/volpetrack">VolpeTrack: AI Budget Tracker</Link></LinkItem>
              <LinkItem><Link to="/holdthen">HoldThen: B2B Logistics Platform</Link></LinkItem>
              <LinkItem>
                <a href="https://apps.apple.com/us/app/volpetrack-ai-budget-tracker/id6748869391" target="_blank" rel="noopener noreferrer">
                  VolpeTrack on App Store
                </a>
              </LinkItem>
              <LinkItem>
                <a href="https://play.google.com/store/apps/details?id=com.volpetrack.mobile" target="_blank" rel="noopener noreferrer">
                  VolpeTrack on Google Play
                </a>
              </LinkItem>
              <LinkItem>
                <a href="https://www.holdthen.com/" target="_blank" rel="noopener noreferrer">
                  HoldThen Website
                </a>
              </LinkItem>
            </LinkList>
          </SitemapSection>

          {/* Legal - Company */}
          <SitemapSection variants={itemVariants}>
            <SectionHeader>
              <FiShield />
              <h2>Legal: XADevs</h2>
            </SectionHeader>
            <LinkList>
              <LinkItem><Link to="/privacy-policy">Privacy Policy</Link></LinkItem>
              <LinkItem><Link to="/terms-of-service">Terms of Service</Link></LinkItem>
            </LinkList>
          </SitemapSection>

          {/* Legal - VolpeTrack */}
          <SitemapSection variants={itemVariants}>
            <SectionHeader>
              <FiFileText />
              <h2>Legal: VolpeTrack</h2>
            </SectionHeader>
            <LinkList>
              <LinkItem><Link to="/volpetrack/privacy">Privacy Policy</Link></LinkItem>
              <LinkItem><Link to="/volpetrack/terms">Terms & Conditions</Link></LinkItem>
              <LinkItem><Link to="/volpetrack/account-deletion">Account Deletion</Link></LinkItem>
            </LinkList>
          </SitemapSection>

          {/* Support & Help */}
          <SitemapSection variants={itemVariants}>
            <SectionHeader>
              <FiHelpCircle />
              <h2>Support & Help</h2>
            </SectionHeader>
            <LinkList>
              <LinkItem><Link to="/support">Support Center</Link></LinkItem>
              <LinkItem>
                <a href="mailto:support@xadevs.com">Email Support</a>
              </LinkItem>
              <LinkItem>
                <a href="mailto:privacy@xadevs.com">Privacy Inquiries</a>
              </LinkItem>
            </LinkList>
          </SitemapSection>

          {/* Resources */}
          <SitemapSection variants={itemVariants}>
            <SectionHeader>
              <FiMap />
              <h2>Resources</h2>
            </SectionHeader>
            <LinkList>
              <LinkItem><Link to="/sitemap">Sitemap</Link></LinkItem>
              <LinkItem>
                <a href="https://github.com/xadevs" target="_blank" rel="noopener noreferrer">
                  GitHub
                </a>
              </LinkItem>
              <LinkItem>
                <a href="https://x.com/XADevs" target="_blank" rel="noopener noreferrer">
                  X (Twitter)
                </a>
              </LinkItem>
            </LinkList>
          </SitemapSection>
          </SitemapGrid>
        </ContentWrapper>
      </PageContainer>
    </>
  );
};

export default Sitemap;
